import { useMemo, useState } from "react";
import { movieGenres, movies } from "@/mocks/home";
import SectionHeading from "@/pages/home/components/SectionHeading";

export default function MoviesSection() {
  const [genre, setGenre] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    return movies.filter((movie) => {
      const matchGenre = genre === "全部" || movie.genre === genre;
      const matchKey =
        key.length === 0 ||
        movie.title.toLowerCase().includes(key) ||
        movie.desc.toLowerCase().includes(key) ||
        movie.meta.toLowerCase().includes(key);
      return matchGenre && matchKey;
    });
  }, [genre, keyword]);

  return (
    <section id="movies" className="scroll-mt-24 bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full px-4 md:px-8">
        <SectionHeading
          eyebrow="MOVIE LIBRARY"
          title="电影大全：按类型快速找到想看的片子"
          desc="电影大全按科幻、悬疑、剧情、动作、爱情、喜剧、动画等类型整理，切换标签即可重新排列片单，也可以直接输入片名或关键词。所有筛选都只作用于本页已收录的影片。"
        />

        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="电影类型筛选">
            {movieGenres.map((item) => {
              const isActive = genre === item;
              return (
                <button
                  key={item}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setGenre(item)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-500 text-foreground-950"
                      : "bg-background-50 text-foreground-700 hover:bg-primary-100 hover:text-primary-800"
                  }`}
                >
                  {item}
                </button>
              );
            })}
          </div>

          <div className="relative w-full lg:w-72">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索片名或关键词"
              aria-label="搜索电影"
              className="w-full rounded-md border border-background-300/70 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
        </div>

        <p className="mt-4 text-xs text-foreground-500">
          共 <span className="font-semibold text-primary-700">{filtered.length}</span> 部影片
          {genre !== "全部" && <span> · 类型「{genre}」</span>}
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4">
            {filtered.map((movie) => (
              <article
                key={movie.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="relative aspect-[3/4] w-full overflow-hidden">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} 电影海报`}
                    title={`${movie.title} 电影海报`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-2 top-2 rounded-full bg-foreground-950/70 px-2 py-0.5 font-label text-xs text-background-50">
                    {movie.genre}
                  </span>
                  <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-accent-500 px-2 py-0.5 font-label text-xs font-bold text-background-50">
                    <i className="ri-star-fill"></i>
                    {movie.rating}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="truncate font-heading text-base font-bold text-foreground-950">{movie.title}</h3>
                  <p className="mt-1 text-xs text-foreground-500">{movie.meta}</p>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{movie.desc}</p>
                  <div className="mt-3 flex items-center justify-between border-t border-background-200/70 pt-3">
                    <span className="text-xs text-foreground-500">{movie.region}</span>
                    <span className="flex items-center gap-1 text-xs font-medium text-primary-700">
                      <i className="ri-calendar-line"></i>
                      {movie.year}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 p-10 text-center">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            <p className="mt-3 text-sm text-foreground-600">没有找到匹配的影片，试试换个关键词或选择「全部」类型。</p>
            <button
              type="button"
              onClick={() => {
                setGenre("全部");
                setKeyword("");
              }}
              className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}